import { Modal } from "react-bootstrap";
import { RiCloseLargeFill } from "react-icons/ri";
import { useState } from "react";
import toast from "react-hot-toast";
import DepositServices from "../../../services/DepositServices";

const money = (v) => Number(v || 0).toFixed(2);

/**
 * Approve / reject confirmation.
 * Approve shows the exact wallet movement first - it debits the approver's own
 * wallet, so it should never be a blind click.
 */
const DepositActionModal = ({ row, mode, onClose, onDone }) => {
  const depositApi = new DepositServices();
  const isApprove = mode === "approve";

  const [remark, setRemark] = useState("");
  const [loading, setLoading] = useState(false);
  const hasUtr = Boolean(String(row?.utr_number || "").trim());

  const submit = async () => {
    if (isApprove && !hasUtr) {
      toast.error("Cannot approve without UTR. Ask the player to submit it first.");
      return;
    }
    if (!isApprove && !remark.trim()) {
      toast.error("Please give a reason for rejecting");
      return;
    }
    setLoading(true);
    try {
      const response = isApprove
        ? await depositApi.approveDeposit(row.id, remark.trim() || undefined)
        : await depositApi.rejectDeposit(row.id, remark.trim());

      if (response.isSuccess) {
        toast.success(response.message);
        onDone();
      } else {
        toast.error(response.message);
      }
    } catch (error) {
      toast.error(
        error?.response?.data?.message ||
          `Failed to ${isApprove ? "approve" : "reject"} deposit`,
      );
    } finally {
      setLoading(false);
    }
  };

  return (
    <Modal show onHide={onClose} backdrop="static" centered className="custom-modal">
      <div className="modal-header border-0 pb-0">
        <h5 className="modal-title">
          {isApprove ? "Approve Deposit" : "Reject Deposit"}
        </h5>
        <button type="button" className="btn" onClick={onClose}>
          <RiCloseLargeFill size={20} />
        </button>
      </div>
      <hr className="my-1" />

      <Modal.Body>
        <div className="dp-summary">
          <div className="dp-summary-row">
            <span>Player</span>
            <strong>
              {row.user_name} ({row.game_id})
            </strong>
          </div>
          <div className="dp-summary-row">
            <span>Deposit amount</span>
            <strong>₹{money(row.amount)}</strong>
          </div>
          {Number(row.discount_amount) > 0 && (
            <div className="dp-summary-row">
              <span>Bonus ({Number(row.discount_percentage)}%)</span>
              <strong className="dp-bonus">+₹{money(row.discount_amount)}</strong>
            </div>
          )}
          <div className="dp-summary-row dp-summary-total">
            <span>{isApprove ? "Will be credited" : "Requested"}</span>
            <strong>₹{money(row.credited_amount)}</strong>
          </div>
          <div className="dp-summary-row">
            <span>UTR</span>
            {row.utr_number ? (
              <strong>{row.utr_number}</strong>
            ) : (
              <strong className="dp-no-utr">Not submitted</strong>
            )}
          </div>
          <div className="dp-summary-row">
            <span>Paid to</span>
            <strong>
              {row.method} · {row.account_name}
            </strong>
          </div>
        </div>

        {isApprove && !hasUtr && (
          <p className="dp-warn dp-warn-danger dp-warn-utr">
            <strong>UTR not submitted.</strong> Approval is blocked until the
            player submits a reference number you can match against your bank
            statement.
          </p>
        )}

        {isApprove ? (
          <p className="dp-warn">
            ₹{money(row.credited_amount)} will be debited from{" "}
            <strong>{row.parent_name || "the parent account"}</strong> and
            credited to the player.
            {row.utr_number
              ? " Confirm the UTR appears in your bank statement before approving."
              : ""}
          </p>
        ) : (
          <p className="dp-warn dp-warn-danger">
            No wallet change. The player will see your reason.
          </p>
        )}

        <label className="form-label mt-2">
          {isApprove ? "Remark (optional)" : "Reason for rejection"}
          {!isApprove && <span className="text-danger"> *</span>}
        </label>
        <textarea
          className="form-control"
          rows={2}
          value={remark}
          onChange={(e) => setRemark(e.target.value)}
          placeholder={
            isApprove
              ? "e.g. verified in bank statement"
              : "e.g. UTR not found in bank statement"
          }
          maxLength={500}
        />
      </Modal.Body>

      <div className="modal-footer border-0">
        <button
          type="button"
          className="btn btn-light px-4"
          onClick={onClose}
          disabled={loading}
        >
          Cancel
        </button>
        <button
          type="button"
          className={`btn px-4 ${isApprove ? "btn-success" : "btn-danger"}`}
          onClick={submit}
          disabled={loading || (isApprove && !hasUtr)}
        >
          {loading
            ? isApprove
              ? "Approving..."
              : "Rejecting..."
            : isApprove
              ? "Approve & Credit"
              : "Reject"}
        </button>
      </div>
    </Modal>
  );
};

export default DepositActionModal;
